import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BlogSidebar from '@/pages/blog/components/BlogSidebar';
import { blogPosts } from '@/mocks/blogPosts';
import NotFound from '@/pages/NotFound';

function triggerAIAgent() {
  const btn = document.querySelector<HTMLButtonElement>('#vapi-widget-floating-button');
  if (btn) btn.click();
}

export default function BlogPost() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <NotFound />;
  }

  const relatedPosts = blogPosts
    .filter((p) => p.category === post.category && p.slug !== post.slug)
    .slice(0, 2);

  const postLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `https://debugin.com/blog/${post.slug}#article`,
    'headline': post.title,
    'description': post.excerpt,
    'image': post.imageUrl,
    'datePublished': post.date,
    'dateModified': post.date,
    'author': {
      '@type': 'Person',
      'name': post.author,
    },
    'publisher': {
      '@id': 'https://debugin.com/#organization',
    },
    'url': `https://debugin.com/blog/${post.slug}`,
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://debugin.com/blog/${post.slug}`,
    },
    'keywords': post.keywords,
    'wordCount': post.content.split(/\s+/).length,
    'inLanguage': 'en',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <title>{`${post.title} | Texio Blog`}</title>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(postLd) }}
      />

      <Navbar />
      <main>
        <article className="pt-24 md:pt-32 pb-16 md:pb-24">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                {/* Main Content */}
                <div className="flex-1 max-w-3xl">
                  {/* Back Link */}
                  <Link
                    to="/blog"
                    className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors duration-200 mb-6 cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    {t('blog.back_to_blog')}
                  </Link>

                  {/* Category + Meta */}
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="bg-primary-100 text-primary-700 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                      {post.category}
                    </span>
                    <span className="text-xs text-foreground-500">
                      {post.date}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-xs text-foreground-500">
                      {post.readTime}
                    </span>
                  </div>

                  {/* Title */}
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold leading-tight tracking-tight text-foreground-950 mb-4">
                    {post.title}
                  </h1>

                  {/* Author */}
                  <div className="flex items-center gap-3 mb-8 pb-6 border-b border-background-200/70">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                      <span className="text-primary-600 text-sm font-bold">
                        {post.author.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">
                        {post.author}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {t('blog.published')} {post.date}
                      </p>
                    </div>
                  </div>

                  {/* Featured Image */}
                  <div className="rounded-xl overflow-hidden mb-8">
                    <img
                      src={post.imageUrl}
                      alt={post.imageAlt}
                      title={post.title}
                      className="w-full h-auto object-cover"
                    />
                  </div>

                  {/* Article Content */}
                  <div
                    className="prose max-w-none
                      prose-headings:text-foreground-950 prose-headings:font-bold prose-headings:tracking-tight
                      prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                      prose-p:text-foreground-700 prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:mb-4
                      prose-strong:text-foreground-900
                      prose-li:text-foreground-700 prose-li:text-sm md:prose-li:text-base prose-li:leading-relaxed
                      prose-ul:mb-6 prose-ol:mb-6
                      prose-table:text-sm prose-table:border prose-table:border-background-200
                      prose-th:bg-background-100 prose-th:text-foreground-950 prose-th:font-semibold prose-th:text-xs prose-th:px-3 prose-th:py-2
                      prose-td:text-foreground-700 prose-td:text-xs prose-td:px-3 prose-td:py-2 prose-td:border prose-td:border-background-200"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />

                  {/* Share */}
                  <div className="mt-10 pt-6 border-t border-background-200/70 flex items-center gap-4">
                    <span className="text-sm font-semibold text-foreground-700">
                      {t('blog.share')}:
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                      }}
                      className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200 cursor-pointer"
                      aria-label="Copy link"
                    >
                      <i className="ri-link text-sm"></i>
                    </button>
                    <a
                      href={`https://linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                      aria-label="Share on LinkedIn"
                    >
                      <i className="ri-linkedin-fill text-sm"></i>
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                      aria-label="Share on Twitter"
                    >
                      <i className="ri-twitter-x-fill text-sm"></i>
                    </a>
                  </div>

                  {/* Bottom CTA */}
                  <div className="mt-10 bg-primary-500 rounded-xl p-6 md:p-8 text-center">
                    <p className="text-lg md:text-xl font-bold text-background-50 mb-2">
                      {t('blog.cta_title')}
                    </p>
                    <p className="text-sm text-background-50/80 mb-5 max-w-md mx-auto">
                      {t('blog.cta_text')}
                    </p>
                    <button
                      onClick={triggerAIAgent}
                      className="inline-flex items-center gap-2 bg-background-50 text-primary-600 px-6 py-3 rounded-full text-sm font-semibold hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      {t('blog.cta_button')}
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>

                  {/* Related Articles */}
                  {relatedPosts.length > 0 && (
                    <div className="mt-12">
                      <h3 className="text-xl font-bold text-foreground-950 mb-5">
                        {t('blog.related_articles')}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {relatedPosts.map((rp) => (
                          <Link
                            key={rp.slug}
                            to={`/blog/${rp.slug}`}
                            className="group bg-white rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200/60 transition-all duration-300"
                          >
                            <div className="h-40 overflow-hidden">
                              <img
                                src={rp.imageUrl}
                                alt={rp.imageAlt}
                                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                                loading="lazy"
                              />
                            </div>
                            <div className="p-4">
                              <p className="text-sm font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                                {rp.title}
                              </p>
                              <p className="text-xs text-foreground-500 mt-1">{rp.date}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <div className="w-full lg:w-72 shrink-0">
                  <BlogSidebar currentSlug={post.slug} />
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}